"use client";

import { usePostHog } from "posthog-js/react";
import {
	SUPPORT_LAUNCHER_BUTTON_CLASS,
	SupportLauncher,
} from "@/components/support/support-launcher";

/**
 * Surfaces this CTA renders on today (WO-4a, 2026-09-22). Kept as its own
 * narrow union — not the full `SupportSurface` — so a new placement here must
 * be a deliberate addition to both this list and `SupportSurface`.
 */
export type B2BContactSurface =
	| "footer"
	| "advertise"
	| "employers_partnership";

interface B2BContactCtaProps {
	locale: "id" | "en";
	surface: B2BContactSurface;
	/** Compact single-line link (footer) vs a small bordered callout (in-page). */
	variant?: "inline" | "card";
	className?: string;
}

const COPY = {
	en: {
		heading: "Doesn't fit? Partnership idea?",
		body: "Not an ad buy, not a hire — something else entirely? Tell us.",
		cta: "Talk to us",
		inline: "Partnership idea? Talk to us",
	},
	id: {
		heading: "Tidak cocok? Ada ide kolaborasi?",
		body: "Bukan iklan, bukan rekrutmen — ada hal lain? Ceritakan ke kami.",
		cta: "Ngobrol dengan kami",
		inline: "Ada ide kolaborasi? Ngobrol dengan kami",
	},
} as const;

/**
 * Low-friction B2B inbound-contact entry point — for the visitor whose intent
 * doesn't fit any existing funnel (not an ad buy on /advertise, not a hire
 * search in the recruiter chat, not a support ticket). Evidence this exists to
 * close: only 4 `job_submissions` ever (0 in the trailing 7 days), and an
 * inbound employer reply once sat 4-5 days unseen before anyone acted on it.
 *
 * Reuses the existing `SupportLauncher` (PostHog chat, mailto fallback) rather
 * than a new form — there is no lead data to collect here, only "someone
 * wants to talk". `onOpened` layers the higher-signal `b2b_contact_opened`
 * event on top of the launcher's own generic `support_widget_opened` capture,
 * and fans it out to Telegram via the existing `/api/telemetry/notify` route
 * handler (never a server action — server-action ids die on every deploy,
 * #5392) — the same route `src/lib/analytics/hooks.ts` already uses for every
 * other client-captured event that also needs a real-time ping.
 */
export function B2BContactCta({
	locale,
	surface,
	variant = "card",
	className,
}: B2BContactCtaProps) {
	const posthog = usePostHog();
	const t = COPY[locale];

	function handleOpened(channel: "chat" | "mailto") {
		try {
			posthog?.capture?.("b2b_contact_opened", { surface, channel });
		} catch {
			// Telemetry must never block someone trying to reach out.
		}

		// Fire-and-forget: the browser already sent the event to PostHog above;
		// this route only handles the Telegram fan-out (can't be done from the
		// browser without leaking the bot token). Best-effort — never awaited,
		// never blocks the click.
		try {
			fetch("/api/telemetry/notify", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					event: "b2b_contact_opened",
					properties: { surface, channel },
				}),
				keepalive: true,
			}).catch(() => undefined);
		} catch {
			// Same contract as above.
		}
	}

	if (variant === "inline") {
		return (
			<SupportLauncher
				surface={surface}
				mailtoSubject={locale === "id" ? "Ide kolaborasi" : "Partnership idea"}
				onOpened={handleOpened}
				className={
					className ??
					"text-sm text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
				}
			>
				{t.inline}
			</SupportLauncher>
		);
	}

	return (
		<div
			className={
				className ??
				"rounded-xl border border-dashed border-border bg-card/50 p-5 text-center"
			}
		>
			<h2 className="text-base font-semibold text-foreground">{t.heading}</h2>
			<p className="mt-1 text-sm text-muted-foreground">{t.body}</p>
			<SupportLauncher
				surface={surface}
				mailtoSubject={locale === "id" ? "Ide kolaborasi" : "Partnership idea"}
				onOpened={handleOpened}
				className={`mt-4 inline-block ${SUPPORT_LAUNCHER_BUTTON_CLASS}`}
			>
				{t.cta}
			</SupportLauncher>
		</div>
	);
}
